import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Download, Edit, FileDown, Upload, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QuestionsTab({ token }) {
  const [questions, setQuestions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkJson, setBulkJson] = useState('');
  const [uploadMode, setUploadMode] = useState('json'); // 'json' or 'csv'
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [form, setForm] = useState({ category: '', question: '', options: ['', '', '', ''], correct_answer: '', difficulty: 'medium' });

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API}/admin/mcq-questions`, { headers: { Authorization: `Bearer ${token}` } });
      setQuestions(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchQuestions(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    if (!form.category || !form.question || form.options.some(o => !o) || !form.correct_answer) {
      toast.error('Please fill all fields'); return;
    }
    try {
      await axios.post(`${API}/admin/mcq-questions`, {
        ...form, options: form.options
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Question added');
      setShowForm(false);
      setForm({ category: '', question: '', options: ['', '', '', ''], correct_answer: '', difficulty: 'medium' });
      fetchQuestions();
    } catch (err) { toast.error('Failed to add question'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/mcq-questions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Question deleted');
      fetchQuestions();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleBulkDelete = async () => {
    if (selectedQuestions.length === 0) {
      toast.error('No questions selected');
      return;
    }
    if (!window.confirm(`Delete ${selectedQuestions.length} selected questions? This cannot be undone.`)) return;
    try {
      const res = await axios.post(`${API}/admin/mcq-questions/bulk-delete`, selectedQuestions, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted ${res.data.deleted_count} questions`);
      setSelectedQuestions([]);
      fetchQuestions();
    } catch (err) {
      toast.error('Failed to delete questions');
    }
  };

  const toggleSelectAll = () => {
    if (selectedQuestions.length === questions.slice(0, 50).length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questions.slice(0, 50).map(q => q.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedQuestions(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBulkUpload = async () => {
    try {
      const questionsArray = JSON.parse(bulkJson);
      if (!Array.isArray(questionsArray)) {
        toast.error('JSON must be an array of questions');
        return;
      }
      const res = await axios.post(`${API}/admin/mcq-questions/bulk`, questionsArray, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.inserted} questions uploaded successfully`);
      if (res.data.errors && res.data.errors.length > 0) {
        toast.error(`${res.data.errors.length} questions had errors`);
      }
      setShowBulkUpload(false);
      setBulkJson('');
      fetchQuestions();
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to upload questions');
      }
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await axios.post(`${API}/admin/mcq-questions/bulk-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${res.data.inserted} questions uploaded successfully`);
      if (res.data.errors && res.data.errors.length > 0) {
        toast.error(`${res.data.errors.length} rows had errors`);
        console.log('CSV Upload errors:', res.data.errors);
      }
      setShowBulkUpload(false);
      setCsvFile(null);
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const categories = [...new Set(questions.map(q => q.category))];

  const sampleJson = `[
  {
    "question": "What is the primary mechanism of action of botulinum toxin?",
    "options": ["Blocks acetylcholine release", "Stimulates collagen", "Increases blood flow", "Reduces inflammation"],
    "correct_answer": "Blocks acetylcholine release",
    "category": "Botulinum Toxin",
    "difficulty": "medium"
  },
  {
    "question": "Which facial artery is most commonly associated with filler-related blindness?",
    "options": ["Supratrochlear artery", "Facial artery", "Maxillary artery", "Temporal artery"],
    "correct_answer": "Supratrochlear artery",
    "category": "Complication Management",
    "difficulty": "hard"
  }
]`;

  return (
    <div data-testid="admin-questions-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-slate-600"><strong>{questions.length}</strong> questions across <strong>{categories.length}</strong> categories</p>
        <div className="flex gap-2">
          {selectedQuestions.length > 0 && (
            <Button onClick={handleBulkDelete} variant="outline" className="rounded-sm text-sm border-red-300 text-red-600 hover:bg-red-50" data-testid="bulk-delete-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete ({selectedQuestions.length})
            </Button>
          )}
          <Button onClick={() => { setShowBulkUpload(!showBulkUpload); setShowForm(false); }} variant="outline" className="rounded-sm text-sm border-[#C5A572] text-[#C5A572] hover:bg-[#C5A572] hover:text-white" data-testid="bulk-upload-btn">
            <Plus className="h-4 w-4 mr-1" /> Bulk Upload
          </Button>
          <Button onClick={() => { setShowForm(!showForm); setShowBulkUpload(false); }} className="bg-[#1B2A4A] text-white rounded-sm text-sm" data-testid="add-question-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Question
          </Button>
        </div>
      </div>

      {/* Bulk Upload Form */}
      {showBulkUpload && (
        <Card className="imcac-card mb-4 border-2 border-[#C5A572]" data-testid="bulk-upload-form">
          <CardContent className="p-5 space-y-4">
            <h3 className="text-lg font-semibold text-[#1B2A4A]">Bulk Upload MCQ Questions</h3>
            
            {/* Upload Mode Toggle */}
            <div className="flex gap-2">
              <Button 
                variant={uploadMode === 'csv' ? 'default' : 'outline'} 
                onClick={() => setUploadMode('csv')}
                className={`rounded-sm text-sm ${uploadMode === 'csv' ? 'bg-[#1B2A4A] text-white' : ''}`}
              >
                CSV File Upload
              </Button>
              <Button 
                variant={uploadMode === 'json' ? 'default' : 'outline'} 
                onClick={() => setUploadMode('json')}
                className={`rounded-sm text-sm ${uploadMode === 'json' ? 'bg-[#1B2A4A] text-white' : ''}`}
              >
                JSON Paste
              </Button>
            </div>

            {uploadMode === 'csv' ? (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">
                  Upload a CSV file with columns: <code className="bg-slate-100 px-1 rounded">question, option_a, option_b, option_c, option_d, correct_answer, category, difficulty</code>
                </p>
                <p className="text-xs text-slate-400">
                  The correct_answer can be A, B, C, D or the full option text.
                </p>
                <div className="border-2 border-dashed border-slate-200 rounded-sm p-6 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="csv-upload"
                    data-testid="csv-file-input"
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <div className="text-slate-500">
                      {csvFile ? (
                        <p className="text-sm font-medium text-[#1B2A4A]">{csvFile.name}</p>
                      ) : (
                        <>
                          <p className="text-sm">Click to select CSV file</p>
                          <p className="text-xs text-slate-400 mt-1">or drag and drop</p>
                        </>
                      )}
                    </div>
                  </label>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleCsvUpload} 
                    disabled={!csvFile || uploading}
                    className="bg-[#C5A572] text-white rounded-sm text-sm"
                    data-testid="upload-csv-btn"
                  >
                    {uploading ? 'Uploading...' : 'Upload CSV'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowBulkUpload(false)} className="rounded-sm text-sm">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Paste a JSON array of questions. Each question must have: question, options (array), correct_answer, category, difficulty.</p>
                <div>
                  <Label className="text-xs">JSON Array of Questions</Label>
                  <textarea
                    value={bulkJson}
                    onChange={e => setBulkJson(e.target.value)}
                    placeholder={sampleJson}
                    className="w-full h-64 mt-1 p-3 text-sm border border-slate-200 rounded-sm font-mono"
                    data-testid="bulk-json-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleBulkUpload} className="bg-[#C5A572] text-white rounded-sm text-sm" data-testid="upload-json-btn">Upload Questions</Button>
                  <Button variant="outline" onClick={() => setBulkJson(sampleJson)} className="rounded-sm text-sm">Load Sample</Button>
                  <Button variant="outline" onClick={() => setShowBulkUpload(false)} className="rounded-sm text-sm">Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="imcac-card mb-4" data-testid="add-question-form">
          <CardContent className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger className="rounded-sm mt-1 text-sm" data-testid="q-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {['Facial Anatomy','Complication Management','Botulinum Toxin','Dermal Fillers','Laser Physics','Patient Assessment','Ethics & Documentation','Emergency Management','Pharmacology','Infection Control'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Difficulty</Label>
                <Select value={form.difficulty} onValueChange={v => setForm({...form, difficulty: v})}>
                  <SelectTrigger className="rounded-sm mt-1 text-sm" data-testid="q-difficulty"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Question</Label>
              <Input value={form.question} onChange={e => setForm({...form, question: e.target.value})} className="rounded-sm mt-1 text-sm" data-testid="q-text" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {form.options.map((opt, i) => (
                <div key={i}>
                  <Label className="text-xs">Option {String.fromCharCode(65 + i)}</Label>
                  <Input value={opt} onChange={e => { const opts = [...form.options]; opts[i] = e.target.value; setForm({...form, options: opts}); }} className="rounded-sm mt-1 text-sm" data-testid={`q-option-${i}`} />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs">Correct Answer (must match one option exactly)</Label>
              <Input value={form.correct_answer} onChange={e => setForm({...form, correct_answer: e.target.value})} className="rounded-sm mt-1 text-sm" data-testid="q-correct" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAdd} className="bg-[#C5A572] text-white rounded-sm text-sm" data-testid="save-question-btn">Save Question</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-sm text-sm">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={selectedQuestions.length === questions.slice(0, 50).length && questions.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded"
                  data-testid="select-all-questions"
                />
              </TableHead>
              <TableHead className="text-xs font-bold">Category</TableHead>
              <TableHead className="text-xs font-bold">Question</TableHead>
              <TableHead className="text-xs font-bold">Difficulty</TableHead>
              <TableHead className="text-xs font-bold w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {questions.slice(0, 50).map((q) => (
              <TableRow key={q.id} className={selectedQuestions.includes(q.id) ? 'bg-blue-50' : ''}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selectedQuestions.includes(q.id)}
                    onChange={() => toggleSelect(q.id)}
                    className="rounded"
                    data-testid={`select-q-${q.id}`}
                  />
                </TableCell>
                <TableCell><Badge variant="outline" className="text-xs rounded-sm">{q.category}</Badge></TableCell>
                <TableCell className="text-sm text-slate-700 max-w-md truncate">{q.question}</TableCell>
                <TableCell>
                  <Badge className={`text-xs rounded-sm ${q.difficulty === 'easy' ? 'bg-green-100 text-green-700' : q.difficulty === 'hard' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{q.difficulty}</Badge>
                </TableCell>
                <TableCell>
                  <button onClick={() => handleDelete(q.id)} className="text-slate-400 hover:text-red-600" data-testid={`del-q-${q.id}`}><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {questions.length > 50 && <p className="text-xs text-slate-400 text-center py-2">Showing first 50 of {questions.length} questions</p>}
      </div>
    </div>
  );
}

export default QuestionsTab;
